/**
 * Net present value, internal rate of return and payback for a list of cash flows.
 *
 * `flows[0]` is the cash flow at time 0 and is not discounted; `flows[t]` is discounted
 * t periods. That is the textbook convention. Excel's `NPV(rate, values)` instead
 * discounts the first value by one period, so `NPV(rate, flows.slice(1)) + flows[0]`
 * equals `npv(rate, flows)` here.
 */

/** Lowest rate searched for an IRR: −99% per period. */
export const IRR_MIN = -0.99;
/** Highest rate searched for an IRR: 10,000% per period. */
export const IRR_MAX = 100;
const GRID_STEPS = 6000;
const ROOT_ITERATIONS = 200;

export function npv(rate: number, flows: readonly number[]): number {
  let total = 0;
  for (const [t, flow] of flows.entries()) {
    total += flow / (1 + rate) ** t;
  }
  return total;
}

/** How many times the cash flows change sign, skipping zeros. */
export function signChanges(flows: readonly number[]): number {
  let changes = 0;
  let last = 0;
  for (const flow of flows) {
    const sign = Math.sign(flow);
    if (sign !== 0) {
      if (last !== 0 && sign !== last) {
        changes += 1;
      }
      last = sign;
    }
  }
  return changes;
}

function bisect(
  f: (r: number) => number,
  lo: number,
  hi: number,
  fLo: number
): number {
  let a = lo;
  let b = hi;
  let fa = fLo;
  for (let k = 0; k < ROOT_ITERATIONS; k += 1) {
    const mid = (a + b) / 2;
    const fm = f(mid);
    if (fm === 0 || (b - a) / 2 < 1e-15) {
      return mid;
    }
    if (Math.sign(fm) === Math.sign(fa)) {
      a = mid;
      fa = fm;
    } else {
      b = mid;
    }
  }
  return (a + b) / 2;
}

/**
 * Every rate in [−99%, 10,000%] at which NPV is zero, smallest first.
 *
 * The rate grid is uniform in ln(1 + r) so that low and high rates are sampled evenly,
 * each sign change is bracketed and refined by bisection (which cannot diverge the way
 * Newton's method can). A root where NPV only touches zero without crossing (a double
 * root) or two roots closer together than one grid step can be missed.
 */
export function findIrrs(flows: readonly number[]): number[] {
  if (signChanges(flows) === 0) {
    return [];
  }
  const f = (r: number) => npv(r, flows);
  const lo = Math.log1p(IRR_MIN);
  const hi = Math.log1p(IRR_MAX);
  const roots: number[] = [];
  let prevR = Math.expm1(lo);
  let prevF = f(prevR);
  if (prevF === 0) {
    roots.push(prevR);
  }
  for (let k = 1; k <= GRID_STEPS; k += 1) {
    const r = Math.expm1(lo + ((hi - lo) * k) / GRID_STEPS);
    const value = f(r);
    if (value === 0) {
      roots.push(r);
    } else if (prevF !== 0 && Math.sign(value) !== Math.sign(prevF)) {
      roots.push(bisect(f, prevR, r, prevF));
    }
    prevR = r;
    prevF = value;
  }
  return roots;
}

/** The IRR Excel would most likely report: the root nearest its 10% default guess. */
export function nearestIrr(
  irrs: readonly number[],
  guess = 0.1
): number | null {
  let best: number | null = null;
  for (const irr of irrs) {
    if (best === null || Math.abs(irr - guess) < Math.abs(best - guess)) {
      best = irr;
    }
  }
  return best;
}

/**
 * Periods until the running total of (optionally discounted) flows turns non-negative,
 * with linear interpolation inside the period. Null when it never does.
 */
export function paybackPeriod(
  flows: readonly number[],
  rate = 0
): number | null {
  let cumulative = 0;
  for (const [t, flow] of flows.entries()) {
    const value = flow / (1 + rate) ** t;
    const before = cumulative;
    cumulative += value;
    if (cumulative >= 0) {
      if (t === 0 || before >= 0) {
        return 0;
      }
      return t - 1 + -before / value;
    }
  }
  return null;
}

/** PV of the later flows ÷ the initial outlay; null unless flows[0] is an outlay. */
export function profitabilityIndex(
  rate: number,
  flows: readonly number[]
): number | null {
  const outlay = flows[0];
  if (outlay === undefined || outlay >= 0) {
    return null;
  }
  return (npv(rate, flows) - outlay) / -outlay;
}

export function npvProfile(
  flows: readonly number[],
  from: number,
  to: number,
  steps: number
): { rate: number; npv: number }[] {
  const points: { rate: number; npv: number }[] = [];
  for (let k = 0; k <= steps; k += 1) {
    const rate = from + ((to - from) * k) / steps;
    points.push({ rate, npv: npv(rate, flows) });
  }
  return points;
}
